// Phase 15 – Production Readiness: System Health, Monitoring, Error Logs,
// Backups, Restore, Release Notes, QA, UAT Feedback, Maintenance Mode,
// Readiness Dashboard.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Activity, Server, Database, HardDrive, ShieldCheck, AlertTriangle, RefreshCw,
  ClipboardCheck, MessageSquarePlus, Rocket, Settings2, Bug, Play, CheckCircle2, XCircle,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";

const STATUS_COLORS = {
  healthy: "bg-green-100 text-green-700",
  operational: "bg-green-100 text-green-700",
  warning: "bg-amber-100 text-amber-700",
  critical: "bg-rose-100 text-rose-700",
  down: "bg-rose-100 text-rose-700",
  not_configured: "bg-slate-100 text-slate-600",
};

// ============================================================
// PRODUCTION READINESS DASHBOARD
// ============================================================

export function ProductionReadinessPage() {
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/production-readiness"); setD(data.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!d) return <DashboardLayout title="Production Readiness"><LoadingBlock /></DashboardLayout>;
  const stateColor = d.readiness_state === "production_ready" ? "bg-green-100 text-green-700"
    : d.readiness_state === "controlled_launch" ? "bg-blue-100 text-blue-700"
    : d.readiness_state === "almost_ready" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700";
  return (
    <DashboardLayout title="Production Readiness" breadcrumbs={["Admin", "Production Readiness"]}>
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#101876] to-[#0d1360] p-6 text-white mb-4" data-testid="prod-readiness-hero">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest opacity-70">Readiness Score</div>
            <div className="text-5xl font-bold">{d.readiness_score}%</div>
            <div className="mt-1 text-sm opacity-80">{d.completed_items} of {d.total_items} checks passed</div>
          </div>
          <div>
            <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${stateColor}`} data-testid="prod-state">{d.readiness_state.replace(/_/g, " ")}</span>
            <div className="mt-2 text-[10px] opacity-70">v{d.version} · {d.environment}</div>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="prod-checklist">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Readiness checklist</div>
        <div className="space-y-1">
          {d.checklist.map((c, i) => (
            <div key={i} className="flex items-center justify-between px-2 py-1.5 hover:bg-slate-50 rounded" data-testid={`prod-item-${i}`}>
              <div className="flex items-center gap-2">
                {c.status === "done" ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                <span className="text-sm">{c.item}</span>
                {c.note && <span className="text-[10px] text-slate-500">({c.note})</span>}
              </div>
              <span className={`text-[10px] font-bold uppercase ${c.status === "done" ? "text-green-700" : "text-amber-600"}`}>{c.status}</span>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// SYSTEM HEALTH SUMMARY
// ============================================================

export function SystemHealthDashboardPage() {
  const [d, setD] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/health/summary"); setD(data.data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, []);
  if (!d) return <DashboardLayout title="System Health"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="System Health" breadcrumbs={["Admin", "Health"]}>
      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs text-slate-500">Overall: <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_COLORS[d.overall] || "bg-slate-100"}`} data-testid="health-overall">{d.overall}</span></div>
        <button type="button" onClick={load} data-testid="health-refresh" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"><RefreshCw className="inline h-3 w-3 mr-1" />Refresh</button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="health-cards">
        {d.checks.map((c, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`health-${c.service_name}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold text-slate-800">{c.service_name}</div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_COLORS[c.status] || "bg-slate-100"}`}>{c.status}</span>
            </div>
            {c.message && <div className="mt-1 text-[11px] text-slate-500">{c.message}</div>}
            {c.response_time_ms != null && <div className="mt-1 text-[10px] text-slate-400">Response: {c.response_time_ms}ms</div>}
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// MONITORING DASHBOARD
// ============================================================

export function MonitoringDashboardPage() {
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/monitoring/dashboard"); setD(data.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!d) return <DashboardLayout title="Monitoring"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Monitoring — Last 24h" breadcrumbs={["Admin", "Monitoring"]}>
      <div className="grid gap-3 md:grid-cols-3" data-testid="monitoring-cards">
        <StatCard label="Total Logins (24h)" value={d.total_logins_24h} icon={Activity} tint="navy" />
        <StatCard label="Failed Logins (24h)" value={d.failed_logins_24h} icon={AlertTriangle} tint={d.failed_logins_24h > 10 ? "rose" : "amber"} />
        <StatCard label="Error Logs (24h)" value={d.error_logs_24h} icon={Bug} tint={d.error_logs_24h > 20 ? "rose" : "amber"} />
        <StatCard label="Critical Errors (24h)" value={d.critical_errors_24h} icon={AlertTriangle} tint="rose" />
        <StatCard label="Unauthorized Attempts (24h)" value={d.unauthorized_attempts_24h} icon={ShieldCheck} tint={d.unauthorized_attempts_24h > 0 ? "amber" : "green"} />
        <StatCard label="Active Users" value={d.active_users} icon={Server} tint="green" />
      </div>
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Last successful backup</div>
        <div className="text-sm">{d.last_backup_at !== "—" ? new Date(d.last_backup_at).toLocaleString() : "No backup yet"} · <span className="text-slate-500">{d.last_backup_type}</span></div>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// ERROR LOGS
// ============================================================

export function AdminErrorLogsPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/error-logs?limit=100"); setRows(data.data?.logs || []); }
      catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  if (rows === null) return <DashboardLayout title="Error Logs"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Error Logs" breadcrumbs={["Admin", "Errors"]}>
      {rows.length === 0 ? <EmptyState title="No errors" description="System is stable." icon={CheckCircle2} /> :
       <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="error-logs-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-3 py-2">Time</th>
            <th className="text-left px-3 py-2">Module</th>
            <th className="text-left px-3 py-2">Endpoint</th>
            <th className="text-left px-3 py-2">Severity</th>
            <th className="text-left px-3 py-2">Message</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`err-${r.id}`}>
                <td className="px-3 py-2 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">{r.module_name}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.endpoint}</td>
                <td className="px-3 py-2 text-xs"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.severity === "critical" ? "bg-rose-100 text-rose-700" : r.severity === "high" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{r.severity}</span></td>
                <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-md">{r.error_message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}
    </DashboardLayout>
  );
}

// ============================================================
// BACKUP MANAGEMENT
// ============================================================

export function BackupManagementPage() {
  const [rows, setRows] = useState(null);
  const [backupType, setBackupType] = useState("database");
  const load = async () => {
    try { const { data } = await api.get("/admin/backups"); setRows(data.data?.backups || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  const run = async () => {
    try { await api.post("/admin/backups/run", { backup_type: backupType }); toast.success("Backup completed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="Backups"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Backup Management" breadcrumbs={["Admin", "Backups"]}>
      <div className="mb-3 flex gap-2 items-center" data-testid="backup-controls">
        <select value={backupType} onChange={(e) => setBackupType(e.target.value)} data-testid="backup-type"
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          {["database", "files", "certificates", "invoices", "full"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="button" onClick={run} data-testid="backup-run"
                className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0d1360]">
          <Play className="inline h-3.5 w-3.5 mr-1" />Run backup now
        </button>
      </div>
      {rows.length === 0 ? <EmptyState title="No backups yet" description="Run your first backup." icon={HardDrive} /> :
       <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="backups-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-3 py-2">Time</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Name</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Size</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`backup-${r.id}`}>
                <td className="px-3 py-2 text-xs">{new Date(r.completed_at || r.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">{r.backup_type}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.backup_name}</td>
                <td className="px-3 py-2 text-xs"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.status === "completed" ? "bg-green-100 text-green-700" : "bg-rose-100 text-rose-700"}`}>{r.status}</span></td>
                <td className="px-3 py-2 text-xs">{r.file_size ? `${Math.round(r.file_size / 1024)} KB` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}
    </DashboardLayout>
  );
}

// ============================================================
// RESTORE MANAGEMENT
// ============================================================

export function RestoreManagementPage() {
  const [rows, setRows] = useState(null);
  const [backups, setBackups] = useState([]);
  const [pick, setPick] = useState("");
  const load = async () => {
    try {
      const { data } = await api.get("/admin/restore-jobs"); setRows(data.data?.restore_jobs || []);
      const { data: b } = await api.get("/admin/backups"); setBackups(b.data?.backups || []);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  const req = async () => {
    if (!pick) return toast.error("Select a backup");
    try { await api.post("/admin/restore-jobs", { backup_job_id: pick, restore_type: "full" }); toast.success("Restore requested"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const approve = async (id) => {
    if (!confirm("Restore will overwrite current data (placeholder). Continue?")) return;
    try { await api.post(`/admin/restore-jobs/${id}/approve`); toast.success("Restore completed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const cancel = async (id) => {
    try { await api.post(`/admin/restore-jobs/${id}/cancel`); toast.success("Cancelled"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="Restore"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Restore Management" breadcrumbs={["Admin", "Restore"]}>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 mb-3 text-xs text-amber-900">
        <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
        Restore operations may overwrite current data. All restores require explicit Super Admin approval.
      </div>
      <div className="mb-3 flex gap-2" data-testid="restore-request">
        <select value={pick} onChange={(e) => setPick(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm min-w-64" data-testid="restore-backup">
          <option value="">Select backup...</option>
          {backups.map((b) => <option key={b.id} value={b.id}>{`${b.backup_name} · ${b.backup_type} · ${new Date(b.completed_at || b.created_at).toLocaleDateString()}`}</option>)}
        </select>
        <button type="button" onClick={req} data-testid="restore-req-btn" className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white">Request restore</button>
      </div>
      {rows.length === 0 ? <EmptyState title="No restore jobs" description="Restore jobs will appear here." icon={RefreshCw} /> :
       <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="restore-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-3 py-2">Time</th>
            <th className="text-left px-3 py-2">Backup</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`restore-${r.id}`}>
                <td className="px-3 py-2 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs font-mono">{r.backup_job_id.slice(0, 8)}...</td>
                <td className="px-3 py-2 text-xs">{r.restore_type}</td>
                <td className="px-3 py-2 text-xs">{r.status}</td>
                <td className="px-3 py-2 text-right space-x-2">
                  {r.status === "pending_approval" && (
                    <>
                      <button type="button" onClick={() => approve(r.id)} className="text-xs text-[#329112] font-semibold hover:underline" data-testid={`restore-approve-${r.id}`}>Approve</button>
                      <button type="button" onClick={() => cancel(r.id)} className="text-xs text-rose-600 font-semibold hover:underline" data-testid={`restore-cancel-${r.id}`}>Cancel</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}
    </DashboardLayout>
  );
}

// ============================================================
// RELEASE NOTES
// ============================================================

export function ReleaseNotesPage() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/release-notes"); setRows(data.data?.releases || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const payload = { ...form, features_json: (form.features_text || "").split("\n").filter(Boolean),
        bug_fixes_json: (form.bugfixes_text || "").split("\n").filter(Boolean),
        known_issues_json: (form.issues_text || "").split("\n").filter(Boolean) };
      delete payload.features_text; delete payload.bugfixes_text; delete payload.issues_text;
      await api.post("/admin/release-notes", payload);
      toast.success("Release created"); setForm(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="Releases"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Release Notes" breadcrumbs={["Admin", "Releases"]}>
      <div className="mb-3">
        <button type="button" onClick={() => setForm({ version_number: "", release_title: "", status: "draft" })}
                data-testid="release-new" className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white">
          <Rocket className="inline h-3.5 w-3.5 mr-1" />New release
        </button>
      </div>
      <div className="space-y-2" data-testid="releases-list">
        {rows.length === 0 ? <EmptyState title="No releases yet" description="Publish your first release." icon={Rocket} /> :
         rows.map((r) => (
          <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`release-${r.version_number}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-slate-800">v{r.version_number} — {r.release_title}</div>
                <div className="text-[11px] text-slate-500">Released: {new Date(r.release_date || r.created_at).toLocaleDateString()} · Status: {r.status}</div>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.status === "published" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>{r.status}</span>
            </div>
            {r.features_json?.length > 0 && (
              <div className="mt-2"><div className="text-[10px] font-bold uppercase text-slate-500">Features</div>
                <ul className="ml-4 list-disc text-xs text-slate-700">{r.features_json.map((f, i) => <li key={i}>{f}</li>)}</ul></div>
            )}
            {r.bug_fixes_json?.length > 0 && (
              <div className="mt-2"><div className="text-[10px] font-bold uppercase text-slate-500">Bug fixes</div>
                <ul className="ml-4 list-disc text-xs text-slate-700">{r.bug_fixes_json.map((f, i) => <li key={i}>{f}</li>)}</ul></div>
            )}
          </div>
        ))}
      </div>
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 space-y-3" data-testid="release-form">
            <div className="text-sm font-bold">New release</div>
            <input type="text" placeholder="v1.0.0" value={form.version_number} onChange={(e) => setForm({ ...form, version_number: e.target.value })} data-testid="release-version" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input type="text" placeholder="Release title" value={form.release_title} onChange={(e) => setForm({ ...form, release_title: e.target.value })} data-testid="release-title" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <textarea rows={3} placeholder="Features (one per line)" value={form.features_text || ""} onChange={(e) => setForm({ ...form, features_text: e.target.value })} data-testid="release-features" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <textarea rows={3} placeholder="Bug fixes (one per line)" value={form.bugfixes_text || ""} onChange={(e) => setForm({ ...form, bugfixes_text: e.target.value })} data-testid="release-bugfixes" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <textarea rows={2} placeholder="Known issues (one per line)" value={form.issues_text || ""} onChange={(e) => setForm({ ...form, issues_text: e.target.value })} data-testid="release-issues" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="release-status" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="draft">Draft</option><option value="published">Published</option>
            </select>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setForm(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm">Cancel</button>
              <button type="button" onClick={save} data-testid="release-save" className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white">Save</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

// ============================================================
// QA TEST CASE MANAGEMENT
// ============================================================

export function QATestCasesPage() {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/qa/test-cases"); setRows(data.data?.test_cases || []);
      const { data: s } = await api.get("/admin/qa/summary"); setSummary(s.data);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/admin/qa/test-cases", form); toast.success("Test case created"); setForm(null); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const mark = async (id, status) => {
    try { await api.patch(`/admin/qa/test-cases/${id}/status`, { status, actual_result: "", remarks: "" }); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="QA"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="QA Test Cases" breadcrumbs={["Admin", "QA"]}>
      {summary && (
        <div className="grid gap-3 md:grid-cols-5 mb-3" data-testid="qa-summary">
          <StatCard label="Total" value={summary.total} icon={ClipboardCheck} tint="navy" />
          <StatCard label="Passed" value={summary.passed} icon={CheckCircle2} tint="green" />
          <StatCard label="Failed" value={summary.failed} icon={XCircle} tint="rose" />
          <StatCard label="Blocked" value={summary.blocked} icon={AlertTriangle} tint="amber" />
          <StatCard label="Pass %" value={`${summary.pass_percentage}%`} icon={Activity} tint="green" />
        </div>
      )}
      <div className="mb-3">
        <button type="button" onClick={() => setForm({ test_case_code: "", module_name: "", test_title: "", test_type: "manual", status: "not_tested" })}
                data-testid="qa-new" className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white">New test case</button>
      </div>
      {rows.length === 0 ? <EmptyState title="No QA cases" description="Create your first test case." icon={ClipboardCheck} /> :
       <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="qa-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-3 py-2">Code</th>
            <th className="text-left px-3 py-2">Module</th>
            <th className="text-left px-3 py-2">Title</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`qa-${r.test_case_code}`}>
                <td className="px-3 py-2 text-xs font-mono">{r.test_case_code}</td>
                <td className="px-3 py-2 text-xs">{r.module_name}</td>
                <td className="px-3 py-2 text-xs">{r.test_title}</td>
                <td className="px-3 py-2 text-xs">{r.status}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  <button type="button" onClick={() => mark(r.id, "passed")} className="text-[10px] px-2 py-0.5 rounded bg-green-100 text-green-700 hover:bg-green-200" data-testid={`qa-pass-${r.id}`}>Pass</button>
                  <button type="button" onClick={() => mark(r.id, "failed")} className="text-[10px] px-2 py-0.5 rounded bg-rose-100 text-rose-700 hover:bg-rose-200" data-testid={`qa-fail-${r.id}`}>Fail</button>
                  <button type="button" onClick={() => mark(r.id, "blocked")} className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 hover:bg-amber-200" data-testid={`qa-block-${r.id}`}>Block</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 space-y-3" data-testid="qa-form">
            <div className="text-sm font-bold">New QA test case</div>
            <input type="text" placeholder="Code (e.g. TC-AUTH-001)" value={form.test_case_code} onChange={(e) => setForm({ ...form, test_case_code: e.target.value })} data-testid="qa-code" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input type="text" placeholder="Module (e.g. Authentication)" value={form.module_name} onChange={(e) => setForm({ ...form, module_name: e.target.value })} data-testid="qa-module" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input type="text" placeholder="Test title" value={form.test_title} onChange={(e) => setForm({ ...form, test_title: e.target.value })} data-testid="qa-title" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <select value={form.test_type} onChange={(e) => setForm({ ...form, test_type: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
              {["manual", "automated", "smoke", "regression"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setForm(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm">Cancel</button>
              <button type="button" onClick={save} data-testid="qa-save" className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white">Save</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

// ============================================================
// UAT FEEDBACK (Admin)
// ============================================================

export function UATFeedbackAdminPage() {
  const [rows, setRows] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/uat/feedback"); setRows(data.data?.feedback || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  const mark = async (id, status) => {
    try { await api.patch(`/admin/uat/feedback/${id}/status`, { status }); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="UAT"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="UAT Feedback" breadcrumbs={["Admin", "UAT"]}>
      {rows.length === 0 ? <EmptyState title="No UAT feedback" description="Testers can submit feedback from their portal." icon={MessageSquarePlus} /> :
       <div className="space-y-2" data-testid="uat-list">
        {rows.map((r) => (
          <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`uat-${r.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-slate-800">{r.module_name} — {r.feedback_type}</div>
                <div className="text-[11px] text-slate-500">By {r.submitted_by_name} ({r.submitted_by_email}) · {new Date(r.created_at).toLocaleString()}</div>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.priority === "critical" ? "bg-rose-100 text-rose-700" : r.priority === "high" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{r.priority}</span>
            </div>
            <div className="mt-2 text-sm text-slate-700">{r.feedback_text}</div>
            <div className="mt-2 flex justify-between items-center">
              <span className="text-[10px] uppercase text-slate-500">Status: <span className="font-bold">{r.status}</span></span>
              <div className="space-x-1">
                {r.status === "open" && <button type="button" onClick={() => mark(r.id, "in_progress")} className="text-xs text-[#101876] hover:underline font-semibold" data-testid={`uat-progress-${r.id}`}>Start</button>}
                {r.status !== "resolved" && r.status !== "closed" && <button type="button" onClick={() => mark(r.id, "resolved")} className="text-xs text-[#329112] hover:underline font-semibold" data-testid={`uat-resolve-${r.id}`}>Resolve</button>}
                {r.status !== "closed" && <button type="button" onClick={() => mark(r.id, "closed")} className="text-xs text-slate-500 hover:underline" data-testid={`uat-close-${r.id}`}>Close</button>}
              </div>
            </div>
          </div>
        ))}
      </div>}
    </DashboardLayout>
  );
}

// ============================================================
// UAT FEEDBACK (User submit + mine)
// ============================================================

export function UATFeedbackSubmitPage() {
  const [form, setForm] = useState({ module_name: "", feedback_type: "bug", feedback_text: "", priority: "medium" });
  const [mine, setMine] = useState([]);
  const loadMine = async () => {
    try { const { data } = await api.get("/uat/feedback/mine"); setMine(data.data?.feedback || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { loadMine(); }, []);
  const submit = async () => {
    if (!form.module_name || !form.feedback_text) return toast.error("Module & feedback are required");
    try { await api.post("/uat/feedback", form); toast.success("Thank you! Submitted."); setForm({ module_name: "", feedback_type: "bug", feedback_text: "", priority: "medium" }); loadMine(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <DashboardLayout title="Submit UAT Feedback" breadcrumbs={["UAT", "Feedback"]}>
      <div className="grid gap-4 lg:grid-cols-[1fr,340px]" data-testid="uat-submit">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">New feedback</div>
          <input type="text" placeholder="Module (e.g. Course Player)" value={form.module_name} onChange={(e) => setForm({ ...form, module_name: e.target.value })} data-testid="uat-module" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.feedback_type} onChange={(e) => setForm({ ...form, feedback_type: e.target.value })} data-testid="uat-type" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              {["bug", "suggestion", "question", "praise"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="uat-priority" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              {["low", "medium", "high", "critical"].map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <textarea rows={6} placeholder="Describe your feedback..." value={form.feedback_text} onChange={(e) => setForm({ ...form, feedback_text: e.target.value })} data-testid="uat-text" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <button type="button" onClick={submit} data-testid="uat-submit-btn" className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white">Submit feedback</button>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2 h-fit" data-testid="uat-mine">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">My feedback</div>
          {mine.length === 0 ? <div className="text-xs text-slate-500">No submissions yet.</div> :
           mine.slice(0, 10).map((m) => (
            <div key={m.id} className="rounded-lg border border-slate-100 p-2 text-xs" data-testid={`uat-my-${m.id}`}>
              <div className="font-semibold">{m.module_name}</div>
              <div className="text-slate-500 truncate">{m.feedback_text}</div>
              <div className="text-[10px] text-slate-400">{m.status}</div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// MAINTENANCE MODE
// ============================================================

export function MaintenanceModePage() {
  const [m, setM] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/maintenance-mode"); setM(data.data || {}); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  const save = async () => {
    try { await api.put("/admin/maintenance-mode", m); toast.success("Saved"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!m) return <DashboardLayout title="Maintenance"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Maintenance Mode" breadcrumbs={["Admin", "Maintenance"]}>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-2xl space-y-3" data-testid="maint-form">
        <label className="flex items-center justify-between rounded-lg p-2 hover:bg-slate-50 cursor-pointer">
          <span className="text-sm font-semibold">Maintenance mode enabled</span>
          <input type="checkbox" checked={!!m.maintenance_enabled} onChange={() => setM({ ...m, maintenance_enabled: !m.maintenance_enabled })} className="h-5 w-5" data-testid="maint-toggle" />
        </label>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Message to display</label>
          <textarea rows={3} value={m.maintenance_message || ""} onChange={(e) => setM({ ...m, maintenance_message: e.target.value })} data-testid="maint-message" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Start</label>
            <input type="datetime-local" value={m.start_time || ""} onChange={(e) => setM({ ...m, start_time: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
          <div><label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">End</label>
            <input type="datetime-local" value={m.end_time || ""} onChange={(e) => setM({ ...m, end_time: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
        </div>
        <button type="button" onClick={save} data-testid="maint-save" className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white">
          <Settings2 className="inline h-3.5 w-3.5 mr-1" />Save maintenance settings
        </button>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// LAUNCH CHECKLIST
// ============================================================

export function LaunchChecklistPage() {
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/launch-checklist"); setD(data.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!d) return <DashboardLayout title="Launch"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Final Launch Checklist" breadcrumbs={["Admin", "Launch"]}>
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="launch-checklist">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Pre-launch verification</div>
        <div className="space-y-1">
          {d.checklist.map((c, i) => (
            <div key={i} className="flex items-center justify-between px-2 py-1.5 hover:bg-slate-50 rounded" data-testid={`launch-item-${i}`}>
              <div className="flex items-center gap-2">
                <input type="checkbox" className="h-4 w-4" />
                <span className="text-sm">{c.item}</span>
              </div>
              <span className="text-[10px] uppercase text-slate-400 font-bold">{c.status}</span>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
